import { PEDIGREE } from 'src/components/Constants';

// Single source of truth for every NODE label on the pedigree.
//
// Historically the label list was hard-coded in three places (LabelsMenu's
// `labels`+`GROUPS`, LabelConfigurations' `STATIC_KEYS`, and Constants'
// `defaultLabelFilters`). This module is the one place that declares which
// labels exist, their display title, their sidebar group, and — for the
// per-label bold/italic feature — which formatting toggles each offers. The
// panel, the saved configurations, and the renderer all derive from here so the
// list can never drift between them.
//
// d3-FREE on purpose (mirrors labelText.js): it's imported by both React
// components and the pure label helpers.
//
// NOTE: the chart-behaviour switches (showCarrierMarks / showAnalysisOverlay /
// standardNomenclature, carrierMarkStyle) are NOT node labels and deliberately
// live in their own local definitions in LabelsMenu — they have no text glyph to
// format and are not part of this catalogue.

// The formatting toggles offered for an ordinary text label. Kept an open array
// (not a bold|italic union) so a future style — colour, underline — is additive.
const TEXT_FORMATS = ['bold', 'italic'];

// Ordered list. Order WITHIN a group is preserved by GROUPS_DERIVED, so this
// must match the historical group order exactly to keep the panel unchanged.
export const LABEL_DEFINITIONS = [
  // ── Personal ──────────────────────────────────────────────────────────────
  {
    key: 'firstName',
    title: 'First Name',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'lastName',
    title: 'Last Name',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'age',
    title: 'Age',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'upn',
    title: 'UPN',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'dob',
    title: 'DoB',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'dod',
    title: 'DoD',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'personNote',
    title: 'Person Note',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'hospitalNumber',
    title: 'Hospital Number',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    // This label prints ONLY the person's `externalPatientIds` — one line per entry, and nothing at
    // all when that array is empty. It never reads the G number: `externalPatientIds` is a plain
    // [String] written only by import and person-merge, never derived from the family id.
    //
    // Briefly retitled "Full Patient Identifier" on the understanding that it could show either the
    // external ID or the G number. It cannot — the DISPLAY LABEL control that chooses between those
    // two drives the family HEADER (PedigreeChart's headlineId), not any node label — so the name is
    // back to describing what it actually shows.
    //
    // Defaults OFF: an identifier under every individual in the family is noise most users do not
    // want. See defaultLabelFilters.
    key: 'externalPatientId',
    title: 'External Patient ID',
    group: 'Personal',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Genomic ───────────────────────────────────────────────────────────────
  {
    key: 'phenotypesName',
    title: 'Phenotypes Name',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'phenotypesID',
    title: 'Phenotypes ID',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genotypesName',
    title: 'Genotype Symbol',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genotypesID',
    title: 'Genotypes ID',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genotypeCarrierStatus',
    title: 'Carrier Status',
    group: 'Genomic',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Clinical ──────────────────────────────────────────────────────────────
  {
    key: 'diseaseWithAge',
    title: 'Disease (with age of diagnosis)',
    group: 'Clinical',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'confirmedDiagnosis',
    title: 'Clinically Confirmed Diagnosis',
    group: 'Clinical',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'consultationDate',
    title: 'Date of Consultation',
    group: 'Clinical',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Tests ─────────────────────────────────────────────────────────────────
  {
    key: 'tests',
    title: 'Test Name',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'genesTested',
    title: 'Genes Tested',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'variants',
    title: 'Variants',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'testResults',
    title: 'Test Result',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'pedigreeText',
    title: 'Test Comments',
    group: 'Tests',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  // ── Reproductive ──────────────────────────────────────────────────────────
  // Data from pregnancy + fetus records, shown beside a fetus symbol. These annotations used to
  // render UNCONDITIONALLY whenever a repro node existed — there was no way to turn them off.
  // Bringing them into the label system makes them user-controllable like every other label. Only
  // fetus nodes carry the underlying data, so these are inert on people. All of them — including
  // the newer fetusName and donorRole — now default ON; see the note in defaultLabelFilters.
  {
    key: 'pregnancyOutcome',
    title: 'Pregnancy Outcome',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'gestationalAge',
    title: 'Gestational Age',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'fetalSexMethod',
    title: 'Fetal Sex Method',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'fetusKaryotype',
    title: 'Karyotype / Finding',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    // TGCLOUD-1597. An ANONYMOUS donor is a satellite whose role IS its label ("Sperm donor"),
    // drawn in place of a name. An IDENTIFIED donor is a real person who keeps their own name, so
    // the role has nowhere to go — this label prints it beside them, which is what makes a long
    // donor line across the chart readable AND what shows which end of it is the donor. Defaults
    // ON for that second reason; inert on anyone who is not a donor.
    key: 'donorRole',
    title: 'Donor Role',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  },
  {
    key: 'fetusName',
    title: 'Fetus Name',
    group: 'Reproductive',
    formattable: TEXT_FORMATS,
    defaultFormat: {}
  }
];

// Sidebar group order (matches the historical GROUPS array, plus Reproductive appended so the
// existing panel order is unchanged).
export const GROUP_ORDER = [
  'Personal',
  'Genomic',
  'Clinical',
  'Tests',
  'Reproductive'
];

// ── Derived lookups (computed once at module load) ────────────────────────────
export const LABEL_KEYS = LABEL_DEFINITIONS.map((d) => d.key);

export const LABEL_BY_KEY = Object.fromEntries(
  LABEL_DEFINITIONS.map((d) => [d.key, d])
);

// { name, keys } per group, in panel order — drop-in for the old GROUPS const.
export const GROUPS_DERIVED = GROUP_ORDER.map((name) => ({
  name,
  keys: LABEL_DEFINITIONS.filter((d) => d.group === name).map((d) => d.key)
}));

// ── Orderable units (TGCLOUD-1663) ───────────────────────────────────────────
//
// What the user can REORDER is not a label key. Several keys share one rendered
// row and cannot be separated:
//   - firstName + lastName print as one name row
//   - phenotypesName + phenotypesID are two halves of one phenotype row
//   - genotypesName + genotypesID + genotypeCarrierStatus share a genotype row
//   - tests/genesTested/variants/testResults/pedigreeText compose ONE Tests block
// So ordering works on the ROW (or stacked block) a clinician actually sees.
// `keys` is the label keys a unit covers, for the panel's grouping and for
// working out whether a unit has anything to show.
//
// `locked: true` means the unit keeps its slot and offers no drag handle:
//   - `acronym` is a gender-identity marker that reads as part of the symbol
//   - `reproductive` is the structural block under a pregnancy symbol (loss,
//     gestation, sex determination, karyotype, fetus name, donor role), whose
//     internal order follows Bennett rather than preference
// A locked unit still sits in the order array so the layout engine can just
// walk one list; the modal reinserts locked units at their fixed index.
export const ORDERABLE_UNITS = [
  { unit: 'acronym', title: 'Gender identity', keys: [], locked: true },
  { unit: 'name', title: 'Name', keys: ['firstName', 'lastName'] },
  { unit: 'upn', title: 'UPN', keys: ['upn'] },
  { unit: 'dob', title: 'DoB', keys: ['dob'] },
  { unit: 'age', title: 'Age', keys: ['age'] },
  { unit: 'dod', title: 'DoD', keys: ['dod'] },
  {
    unit: 'hospitalNumber',
    title: 'Hospital Number',
    keys: ['hospitalNumber']
  },
  {
    unit: 'externalPatientId',
    title: 'External Patient ID',
    keys: ['externalPatientId'],
    multiValue: true
  },
  {
    unit: 'reproductive',
    title: 'Reproductive details',
    locked: true,
    keys: [
      'pregnancyOutcome',
      'gestationalAge',
      'fetalSexMethod',
      'fetusKaryotype',
      'fetusName',
      'donorRole'
    ]
  },
  {
    unit: 'consultationDate',
    title: 'Date of Consultation',
    keys: ['consultationDate'],
    multiValue: true
  },
  {
    unit: 'confirmedDiagnosis',
    title: 'Diagnosis',
    keys: ['confirmedDiagnosis']
  },
  {
    unit: 'tests',
    title: 'Tests',
    keys: ['tests', 'genesTested', 'variants', 'testResults', 'pedigreeText'],
    multiValue: true
  },
  {
    unit: 'diseaseWithAge',
    title: 'Disease (with age of diagnosis)',
    keys: ['diseaseWithAge'],
    multiValue: true
  },
  {
    unit: 'phenotypes',
    title: 'Phenotypes',
    keys: ['phenotypesName', 'phenotypesID'],
    multiValue: true
  },
  {
    unit: 'genotypes',
    title: 'Genotypes',
    keys: ['genotypesName', 'genotypesID', 'genotypeCarrierStatus'],
    multiValue: true
  },
  { unit: 'personNote', title: 'Person Note', keys: ['personNote'] }
];

// The stacking order used when a preset carries no order of its own — i.e. the
// order the chart has always drawn. Derived from ORDERABLE_UNITS so the two can
// never disagree; `labelOrderGolden.test.js` pins it against the historical
// sequence.
export const DEFAULT_LABEL_ORDER = ORDERABLE_UNITS.map((u) => u.unit);

export const UNIT_BY_KEY = Object.fromEntries(
  ORDERABLE_UNITS.map((u) => [u.unit, u])
);

// Units whose rows the user can reorder (everything but the locked ones).
export const DRAGGABLE_UNITS = ORDERABLE_UNITS.filter((u) => !u.locked).map(
  (u) => u.unit
);

const LOCKED_INDEX = ORDERABLE_UNITS.reduce((acc, u, i) => {
  if (u.locked) acc[u.unit] = i;
  return acc;
}, {});

// Turn a stored (possibly stale, partial or hostile) order into a complete,
// valid one. A preset is a saved document that outlives the code that wrote it,
// so this has to cope with: unknown units from a newer/older build, duplicates,
// units missing because they did not exist when the preset was saved, and a
// locked unit that someone moved.
//
// Missing units are reinserted next to the neighbour they follow by default,
// rather than appended, so a preset saved before a label existed still shows
// that label somewhere sensible instead of at the bottom.
export const sanitiseLabelOrder = (order) => {
  const seen = new Set();
  const kept = (Array.isArray(order) ? order : [])
    .filter((u) => typeof u === 'string' && UNIT_BY_KEY[u])
    .filter((u) => (seen.has(u) ? false : seen.add(u)))
    .filter((u) => !UNIT_BY_KEY[u].locked);

  // Reinstate anything the stored order did not mention.
  for (const unit of DEFAULT_LABEL_ORDER) {
    if (seen.has(unit) || UNIT_BY_KEY[unit].locked) continue;
    const defaultIdx = DEFAULT_LABEL_ORDER.indexOf(unit);
    // The last unit that comes before it by default AND is already placed.
    let at = 0;
    for (let i = 0; i < kept.length; i += 1) {
      if (DEFAULT_LABEL_ORDER.indexOf(kept[i]) < defaultIdx) at = i + 1;
    }
    kept.splice(at, 0, unit);
    seen.add(unit);
  }

  // Locked units go back to their own index, last, so they cannot be displaced.
  const result = kept;
  Object.entries(LOCKED_INDEX)
    .sort((a, b) => a[1] - b[1])
    .forEach(([unit, idx]) =>
      result.splice(Math.min(idx, result.length), 0, unit)
    );

  return result;
};

// The order to lay a chart out in: whatever the preset carries, made safe.
export const effectiveLabelOrder = (labelsSet) =>
  sanitiseLabelOrder(labelsSet?.labelOrder);

// Default visibility for a key, sourced from Constants (one source for both
// axes). Returns undefined for any key absent from defaultLabelFilters, which
// reads as "off" everywhere. Every label defined here now HAS an entry, so an off
// default is always a visible decision — that silent-off path was how
// externalPatientId ended up invisible without anyone choosing it.
export const defaultVisibilityFor = (key) => PEDIGREE.defaultLabelFilters[key];

// Whether a key offers any formatting control at all.
export const isFormattable = (key) =>
  (LABEL_BY_KEY[key]?.formattable || []).length > 0;

// The default labelsSet.formatting map — only emits entries for labels whose
// defaultFormat is non-empty, so with all defaults `{}` this is `{}` and adds
// nothing to the working state (keeps flag-off behaviour byte-identical).
export const defaultFormatting = () =>
  Object.fromEntries(
    LABEL_DEFINITIONS.filter(
      (d) => d.defaultFormat && (d.defaultFormat.bold || d.defaultFormat.italic)
    ).map((d) => [d.key, { ...d.defaultFormat }])
  );
